import { create } from 'zustand';
import { authService } from '../services/authService';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  impersonatedBy: { name: string; email: string } | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
  clearError: () => void;
  impersonate: (userId: string) => Promise<void>;
  exitImpersonation: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  impersonatedBy: JSON.parse(localStorage.getItem('impersonatedBy') || 'null'),

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(email, password);

      // Store tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      set({
        error: errorMessage,
        isLoading: false,
        isAuthenticated: false,
        user: null,
      });
      throw new Error(errorMessage);
    }
  },

  logout: () => {
    authService.logout();
    set({
      user: null,
      isAuthenticated: false,
      error: null,
      impersonatedBy: null,
    });
  },

  setUser: (user: User | null) => {
    set({ user, isAuthenticated: !!user });
  },

  clearError: () => {
    set({ error: null });
  },

  impersonate: async (userId: string) => {
    try {
      // Save current admin token before impersonating
      const currentToken = localStorage.getItem('accessToken');
      if (currentToken) {
        localStorage.setItem('adminToken', currentToken);
      }

      const response = await authService.impersonate(userId);

      // Set impersonation token
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('impersonatedBy', JSON.stringify(response.impersonatedBy));

      set({
        user: response.user,
        isAuthenticated: true,
        impersonatedBy: response.impersonatedBy,
      });
    } catch (error: any) {
      console.error('Impersonation failed:', error);
      throw error;
    }
  },

  exitImpersonation: () => {
    // Restore the original admin token
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      localStorage.setItem('accessToken', adminToken);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('impersonatedBy');

      // Decode admin token to restore user info
      try {
        const payload = JSON.parse(atob(adminToken.split('.')[1]));
        set({
          user: {
            id: payload.userId,
            email: payload.email,
            name: payload.name,
            role: payload.role,
            isAlsoInspector: payload.isAlsoInspector,
          },
          impersonatedBy: null,
        });
      } catch {
        // If decode fails, force logout
        authService.logout();
        set({ user: null, isAuthenticated: false, impersonatedBy: null });
      }
    }
  },
}));
